
import React, { useState } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CalendarIcon, Plus, Trash2, Upload, Building, Users, UserPlus, X, Edit, Eye } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { UploadFile } from '@/integrations/Core';
import { Department, User } from '@/entities/all';

const roleOptions = [
    { value: 'ceo', label: 'CEO / Président Directeur Général' },
    { value: 'dg', label: 'Directeur Général' },
    { value: 'deputy_dg', label: 'Directeur Général Adjoint' },
    { value: 'cfo', label: 'Directeur Financier (DAF)' },
    { value: 'cto', label: 'Directeur Technique' },
    { value: 'coo', label: 'Directeur des Opérations' },
    { value: 'dsi', label: 'Directeur des Systèmes d\'Information (DSI)' },
    { value: 'drh', label: 'Directeur des Ressources Humaines (DRH)' },
    { value: 'capex_referent', label: 'Référent CAPEX' },
    { value: 'dperf', label: 'Directeur Performance' },
    { value: 'quality_manager', label: 'Responsable Qualité' },
    { value: 'other', label: 'Autre' }
];

const departmentRoleOptions = [
    { value: 'head', label: 'Chef de département' },
    { value: 'deputy_head', label: 'Adjoint' },
    { value: 'supervisor', label: 'Superviseur' },
    { value: 'coordinator', label: 'Coordinateur' },
    { value: 'capex_referent', label: 'Référent CAPEX' },
    { value: 'controller', label: 'Contrôleur' },
    { value: 'other', label: 'Autre' }
];

// @ts-nocheck
export default function SubsidiaryForm({ isOpen, onClose, onSubmit, subsidiary, users, departments }) {
    const [currentTab, setCurrentTab] = useState("general");
    const [logoUploading, setLogoUploading] = useState(false);
    const [subsidiaryDepartments, setSubsidiaryDepartments] = useState(
        departments?.filter(d => d.subsidiary_number === subsidiary?.subsidiary_number) || []
    );

    const {
        register,
        handleSubmit,
        control,
        watch,
        setValue,
        formState: { errors },
        reset
    } = useForm({
        defaultValues: subsidiary || {
            name: '',
            code: '',
            subsidiary_number: '',
            color: '#0D9488',
            logo_url: '',
            description: '',
            website: '',
            email: '',
            phone: '',
            address: '',
            city: '',
            postal_code: '',
            country: 'Maroc',
            status: 'active',
            business_creation_date: '',
            closure_date: '',
            industry: '',
            general_managers: []
        }
    });

    const { fields: managerFields, append: addManager, remove: removeManager } = useFieldArray({
        control,
        name: "general_managers"
    });

    const watchedName = watch("name");
    const watchedCode = watch("code");
    const watchedColor = watch("color");

    // Auto-generate code from name
    React.useEffect(() => {
        if (watchedName && !subsidiary) {
            const generatedCode = watchedName.substring(0, 6).toUpperCase().replace(/\s/g, '');
            setValue("code", generatedCode);
        }
    }, [watchedName, setValue, subsidiary]);

    const handleLogoUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setLogoUploading(true);
        try {
            const { file_url } = await UploadFile({ file });
            setValue("logo_url", file_url);
        } catch (error) {
            console.error('Erreur lors du téléchargement:', error);
        } finally {
            setLogoUploading(false);
        }
    };

    const handleFormSubmit = (data) => {
        onSubmit(data);
        reset();
        setCurrentTab("general");
    };

    const handleClose = () => {
        reset();
        setCurrentTab("general");
        onClose();
    };

    // Department management functions
    const handleCreateDepartment = async (deptData) => {
        try {
            const newDept = {
                ...deptData,
                subsidiary_number: watch("subsidiary_number"),
                status: 'active',
                managers: deptData.managers || [] // Ensure managers is an array
            };
            const createdDept = await Department.create(newDept);
            setSubsidiaryDepartments([...subsidiaryDepartments, createdDept]);
        } catch (error) {
            console.error('Erreur lors de la création du département:', error);
        }
    };

    const handleUpdateDepartment = async (deptId, deptData) => {
        try {
            await Department.update(deptId, deptData);
            const updatedDepts = subsidiaryDepartments.map(d =>
                d.id === deptId ? { ...d, ...deptData } : d
            );
            setSubsidiaryDepartments(updatedDepts);
        } catch (error) {
            console.error('Erreur lors de la mise à jour du département:', error);
        }
    };

    const handleDeleteDepartment = async (deptId) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer ce département ?')) {
            try {
                await Department.delete(deptId);
                setSubsidiaryDepartments(subsidiaryDepartments.filter(d => d.id !== deptId));
            } catch (error) {
                console.error('Erreur lors de la suppression du département:', error);
            }
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="text-2xl">
                        {subsidiary ? 'Modifier la filiale' : 'Nouvelle filiale'}
                    </DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit(handleFormSubmit)}>
                    <Tabs value={currentTab} onValueChange={setCurrentTab} className="w-full">
                        <TabsList className="grid w-full grid-cols-3">
                            <TabsTrigger value="general" className="gap-2">
                                <Building className="w-4 h-4" />
                                Général
                            </TabsTrigger>
                            <TabsTrigger value="departments" className="gap-2">
                                <Users className="w-4 h-4" />
                                Départements
                            </TabsTrigger>
                            <TabsTrigger value="managers" className="gap-2">
                                <UserPlus className="w-4 h-4" />
                                Profils & rôles
                            </TabsTrigger>
                        </TabsList>

                        {/* Onglet Général */}
                        <TabsContent value="general" className="space-y-8 mt-6">
                            {/* En-tête avec logo */}
                            <Card className="border-0 shadow-md">
                                <CardContent className="p-6">
                                    <div className="flex items-start gap-8">
                                        {/* Section Logo */}
                                        <div className="flex flex-col items-center space-y-4">
                                            <Label className="text-base font-medium">Logo de la filiale</Label>
                                            <Avatar className="w-24 h-24 border-2 border-gray-200">
                                                {watch("logo_url") ? (
                                                    <img src={watch("logo_url")} alt="Logo" className="w-full h-full object-cover rounded-full" />
                                                ) : (
                                                    <AvatarFallback
                                                        className="text-white font-bold text-lg"
                                                        style={{backgroundColor: watchedColor}}
                                                    >
                                                        {watchedCode?.substring(0, 2) || watchedName?.substring(0, 2) || 'XX'}
                                                    </AvatarFallback>
                                                )}
                                            </Avatar>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => document.getElementById('logo-upload').click()}
                                                disabled={logoUploading}
                                                className="gap-2"
                                            >
                                                <Upload className="w-4 h-4" />
                                                {logoUploading ? 'Téléchargement...' : 'Changer logo'}
                                            </Button>
                                            <input
                                                id="logo-upload"
                                                type="file"
                                                accept="image/*"
                                                onChange={handleLogoUpload}
                                                className="hidden"
                                            />
                                        </div>

                                        {/* Informations principales */}
                                        <div className="flex-1 space-y-6">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div className="space-y-3">
                                                    <Label htmlFor="name" className="text-base font-medium">Nom de la filiale *</Label>
                                                    <Input
                                                        id="name"
                                                        {...register("name", { required: "Le nom est requis" })}
                                                        placeholder="Ex: Socolait Maroc"
                                                        className="h-11"
                                                    />
                                                    {errors.name && <p className="text-red-500 text-sm">{errors.name.message}</p>}
                                                </div>

                                                <div className="space-y-3">
                                                    <Label htmlFor="code" className="text-base font-medium">Code filiale *</Label>
                                                    <Input
                                                        id="code"
                                                        {...register("code", { required: "Le code est requis" })}
                                                        placeholder="SOCOL"
                                                        className="h-11 font-mono"
                                                    />
                                                    {errors.code && <p className="text-red-500 text-sm">{errors.code.message}</p>}
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                <div className="space-y-3">
                                                    <Label htmlFor="subsidiary_number" className="text-base font-medium">Numéro filiale *</Label>
                                                    <Input
                                                        id="subsidiary_number"
                                                        type="number"
                                                        {...register("subsidiary_number", { required: "Le numéro est requis", valueAsNumber: true })}
                                                        placeholder="1"
                                                        className="h-11"
                                                    />
                                                    {errors.subsidiary_number && <p className="text-red-500 text-sm">{errors.subsidiary_number.message}</p>}
                                                </div>

                                                <div className="space-y-3">
                                                    <Label htmlFor="color" className="text-base font-medium">Couleur de marque</Label>
                                                    <div className="flex items-center gap-3">
                                                        <Input
                                                            id="color"
                                                            type="color"
                                                            {...register("color")}
                                                            className="h-11 w-20 p-1 rounded cursor-pointer"
                                                        />
                                                        <span className="text-sm text-gray-600 font-mono">{watchedColor}</span>
                                                    </div>
                                                </div>

                                                <div className="space-y-3">
                                                    <Label htmlFor="status" className="text-base font-medium">Statut</Label>
                                                    <Controller
                                                        name="status"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Select onValueChange={field.onChange} value={field.value}>
                                                                <SelectTrigger className="h-11">
                                                                    <SelectValue />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    <SelectItem value="active">🟢 Active</SelectItem>
                                                                    <SelectItem value="inactive">🔴 Inactive</SelectItem>
                                                                </SelectContent>
                                                            </Select>
                                                        )}
                                                    />
                                                </div>
                                            </div>

                                            <div className="space-y-3">
                                                <Label htmlFor="description" className="text-base font-medium">Description</Label>
                                                <Textarea
                                                    id="description"
                                                    {...register("description")}
                                                    placeholder="Description de l'activité de la filiale"
                                                    rows={3}
                                                    className="resize-none"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Contact et localisation */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                <Card className="border-0 shadow-md">
                                    <CardHeader>
                                        <CardTitle className="text-lg">Informations de contact</CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-5">
                                        <div className="space-y-2">
                                            <Label htmlFor="website" className="text-sm font-medium">Site web</Label>
                                            <Input id="website" {...register("website")} placeholder="https://www.exemple.com" className="h-10" />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="email" className="text-sm font-medium">Email</Label>
                                            <Input id="email" type="email" {...register("email")} placeholder="contact@exemple.com" className="h-10" />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="phone" className="text-sm font-medium">Téléphone</Label>
                                            <Input id="phone" {...register("phone")} placeholder="+212 5XX XX XX XX" className="h-10" />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="industry" className="text-sm font-medium">Secteur d'activité</Label>
                                            <Input id="industry" {...register("industry")} placeholder="Agroalimentaire, IT, etc." className="h-10" />
                                        </div>
                                    </CardContent>
                                </Card>

                                <Card className="border-0 shadow-md">
                                    <CardHeader>
                                        <CardTitle className="text-lg">Adresse</CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-5">
                                        <div className="space-y-2">
                                            <Label htmlFor="address" className="text-sm font-medium">Adresse complète</Label>
                                            <Textarea id="address" {...register("address")} placeholder="Adresse complète" rows={2} className="resize-none" />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label htmlFor="city" className="text-sm font-medium">Ville</Label>
                                                <Input id="city" {...register("city")} placeholder="Casablanca" className="h-10" />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="postal_code" className="text-sm font-medium">Code postal</Label>
                                                <Input id="postal_code" {...register("postal_code")} placeholder="20000" className="h-10" />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="country" className="text-sm font-medium">Pays</Label>
                                            <Input id="country" {...register("country")} placeholder="Maroc" className="h-10" />
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>

                            {/* Dates importantes */}
                            <Card className="border-0 shadow-md">
                                <CardHeader>
                                    <CardTitle className="text-lg">Dates importantes</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="space-y-2">
                                            <Label className="text-sm font-medium">Date de création (business)</Label>
                                            <Controller
                                                name="business_creation_date"
                                                control={control}
                                                render={({ field }) => (
                                                    <Popover>
                                                        <PopoverTrigger asChild>
                                                            <Button variant="outline" className="w-full justify-start text-left font-normal h-10">
                                                                <CalendarIcon className="mr-2 h-4 w-4" />
                                                                {field.value ? format(new Date(field.value), 'PPP', { locale: fr }) : "Sélectionner une date"}
                                                            </Button>
                                                        </PopoverTrigger>
                                                        <PopoverContent className="w-auto p-0">
                                                            <Calendar
                                                                mode="single"
                                                                selected={field.value ? new Date(field.value) : undefined}
                                                                onSelect={(date) => field.onChange(date)}
                                                                locale={fr}
                                                            />
                                                        </PopoverContent>
                                                    </Popover>
                                                )}
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <Label className="text-sm font-medium">Date de clôture (optionnelle)</Label>
                                            <Controller
                                                name="closure_date"
                                                control={control}
                                                render={({ field }) => (
                                                    <Popover>
                                                        <PopoverTrigger asChild>
                                                            <Button variant="outline" className="w-full justify-start text-left font-normal h-10">
                                                                <CalendarIcon className="mr-2 h-4 w-4" />
                                                                {field.value ? format(new Date(field.value), 'PPP', { locale: fr }) : "Sélectionner une date"}
                                                            </Button>
                                                        </PopoverTrigger>
                                                        <PopoverContent className="w-auto p-0">
                                                            <Calendar
                                                                mode="single"
                                                                selected={field.value ? new Date(field.value) : undefined}
                                                                onSelect={(date) => field.onChange(date)}
                                                                locale={fr}
                                                            />
                                                        </PopoverContent>
                                                    </Popover>
                                                )}
                                            />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </TabsContent>

                        {/* Onglet Départements */}
                        <TabsContent value="departments" className="space-y-6 mt-6">
                            <DepartmentManagement
                                subsidiaryNumber={watch("subsidiary_number")}
                                departments={subsidiaryDepartments || []}
                                users={users || []}
                                onCreateDepartment={handleCreateDepartment}
                                onUpdateDepartment={handleUpdateDepartment}
                                onDeleteDepartment={handleDeleteDepartment}
                            />
                        </TabsContent>

                        {/* Onglet Profils & rôles */}
                        <TabsContent value="managers" className="space-y-6 mt-6">
                            <Card className="border-0 shadow-md">
                                <CardHeader>
                                    <div className="flex justify-between items-center">
                                        <CardTitle className="text-lg">Direction générale</CardTitle>
                                        <Button
                                            type="button"
                                            onClick={() => addManager({
                                                user_id: '',
                                                role: 'other',
                                                role_label: '',
                                                start_date: '',
                                                end_date: ''
                                            })}
                                            className="gap-2"
                                        >
                                            <Plus className="w-4 h-4" />
                                            Ajouter un dirigeant
                                        </Button>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    {managerFields.map((field, index) => (
                                        <Card key={field.id} className="mb-4 border border-gray-200">
                                            <CardContent className="p-4">
                                                <div className="flex justify-between items-center mb-4">
                                                    <h5 className="font-medium">Dirigeant #{index + 1}</h5>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => removeManager(index)}
                                                        className="text-red-500 hover:text-red-700"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </Button>
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div className="space-y-2">
                                                        <Label className="text-sm font-medium">Utilisateur</Label>
                                                        <Controller
                                                            name={`general_managers.${index}.user_id`}
                                                            control={control}
                                                            render={({ field }) => (
                                                                <Select onValueChange={field.onChange} value={field.value}>
                                                                    <SelectTrigger>
                                                                        <SelectValue placeholder="Sélectionner un utilisateur" />
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        {users.map(user => (
                                                                            <SelectItem key={user.id} value={user.id}>
                                                                                {user.full_name} ({user.email})
                                                                            </SelectItem>
                                                                        ))}
                                                                    </SelectContent>
                                                                </Select>
                                                            )}
                                                        />
                                                    </div>

                                                    <div className="space-y-2">
                                                        <Label className="text-sm font-medium">Rôle</Label>
                                                        <Controller
                                                            name={`general_managers.${index}.role`}
                                                            control={control}
                                                            render={({ field }) => (
                                                                <Select onValueChange={field.onChange} value={field.value}>
                                                                    <SelectTrigger>
                                                                        <SelectValue placeholder="Sélectionner un rôle" />
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        {roleOptions.map(role => (
                                                                            <SelectItem key={role.value} value={role.value}>
                                                                                {role.label}
                                                                            </SelectItem>
                                                                        ))}
                                                                    </SelectContent>
                                                                </Select>
                                                            )}
                                                        />
                                                    </div>

                                                    <div className="space-y-2">
                                                        <Label className="text-sm font-medium">Libellé personnalisé (optionnel)</Label>
                                                        <Input {...register(`general_managers.${index}.role_label`)} placeholder="Titre spécifique" />
                                                    </div>

                                                    <div className="space-y-2">
                                                        <Label className="text-sm font-medium">Date de début</Label>
                                                        <Controller
                                                            name={`general_managers.${index}.start_date`}
                                                            control={control}
                                                            render={({ field }) => (
                                                                <Popover>
                                                                    <PopoverTrigger asChild>
                                                                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                                                                            <CalendarIcon className="mr-2 h-4 w-4" />
                                                                            {field.value ? format(new Date(field.value), 'PPP', { locale: fr }) : "Sélectionner"}
                                                                        </Button>
                                                                    </PopoverTrigger>
                                                                    <PopoverContent className="w-auto p-0">
                                                                        <Calendar
                                                                            mode="single"
                                                                            selected={field.value ? new Date(field.value) : undefined}
                                                                            onSelect={(date) => field.onChange(date)}
                                                                            locale={fr}
                                                                        />
                                                                    </PopoverContent>
                                                                </Popover>
                                                            )}
                                                        />
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}

                                    {managerFields.length === 0 && (
                                        <div className="text-center py-8 text-gray-500">
                                            <UserPlus className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                                            <p>Aucun dirigeant configuré</p>
                                            <p className="text-sm">Cliquez sur "Ajouter un dirigeant" pour commencer</p>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </TabsContent>
                    </Tabs>

                    <DialogFooter className="pt-6 border-t">
                        <Button type="button" variant="outline" onClick={handleClose}>
                            Annuler
                        </Button>
                        <Button type="submit" className="bg-teal-600 hover:bg-teal-700">
                            {subsidiary ? 'Mettre à jour' : 'Créer la filiale'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

// Composant pour la gestion des départements
function DepartmentManagement({ subsidiaryNumber, departments, users, onCreateDepartment, onUpdateDepartment, onDeleteDepartment }) {
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingDept, setEditingDept] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        code: '',
        description: '',
        managers: []
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        if (editingDept) {
            onUpdateDepartment(editingDept.id, formData);
        } else {
            onCreateDepartment(formData);
        }
        setIsFormOpen(false);
        setEditingDept(null);
        setFormData({ name: '', code: '', description: '', managers: [] });
    };

    const handleEdit = (dept) => {
        setEditingDept(dept);
        setFormData({
            ...dept,
            managers: dept.managers || [] // S'assurer que managers est toujours un tableau
        });
        setIsFormOpen(true);
    };

    const handleAddManager = () => {
        setFormData({
            ...formData,
            managers: [...(formData.managers || []), { user_id: '', role: '', role_label: '', email: '', phone: '' }]
        });
    };

    const handleRemoveManager = (index) => {
        const currentManagers = formData.managers || [];
        const newManagers = currentManagers.filter((_, i) => i !== index);
        setFormData({ ...formData, managers: newManagers });
    };

    const handleOpenForm = () => {
        setEditingDept(null);
        setFormData({ name: '', code: '', description: '', managers: [] });
        setIsFormOpen(true);
    };

    return (
        <Card className="border-0 shadow-md">
            <CardHeader>
                <div className="flex justify-between items-center">
                    <CardTitle className="text-lg">Départements de la filiale</CardTitle>
                    <Button
                        type="button"
                        onClick={handleOpenForm}
                        className="gap-2"
                    >
                        <Plus className="w-4 h-4" />
                        Nouveau département
                    </Button>
                </div>
            </CardHeader>
            <CardContent>
                {departments && departments.length > 0 ? (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Département</TableHead>
                                <TableHead>Responsables</TableHead>
                                <TableHead>Statut</TableHead>
                                <TableHead>Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {departments.map((dept) => (
                                <TableRow key={dept.id}>
                                    <TableCell>
                                        <div>
                                            <p className="font-medium">{dept.name}</p>
                                            <p className="text-sm text-gray-500">{dept.code}</p>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        {dept.managers && dept.managers.length > 0 ? (
                                            <div className="space-y-1">
                                                {dept.managers.map((manager, idx) => {
                                                    const user = users.find(u => u.id === manager.user_id);
                                                    return (
                                                        <div key={idx} className="text-sm">
                                                            <span className="font-medium">{user?.full_name || 'Inconnu'}</span>
                                                            <span className="text-gray-500 text-xs ml-1">({manager.role_label || manager.role})</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <span className="text-gray-400 text-sm">Aucun responsable</span>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Badge className={dept.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                                            {dept.status === 'active' ? 'Actif' : 'Inactif'}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex gap-2">
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handleEdit(dept)}
                                            >
                                                <Edit className="w-4 h-4" />
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => onDeleteDepartment(dept.id)}
                                                className="text-red-600 hover:text-red-700"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                ) : (
                    <div className="text-center py-8 text-gray-500">
                        <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p>Aucun département configuré pour cette filiale</p>
                    </div>
                )}

                {/* Formulaire département */}
                {isFormOpen && (
                    <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
                        <DialogContent className="max-w-4xl">
                            <DialogHeader>
                                <DialogTitle>{editingDept ? 'Modifier le département' : 'Nouveau département'}</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Nom du département</Label>
                                        <Input
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Code</Label>
                                        <Input
                                            value={formData.code}
                                            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Description</Label>
                                    <Textarea
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    />
                                </div>

                                {/* Responsables */}
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center">
                                        <Label>Responsables du département</Label>
                                        <Button type="button" variant="outline" size="sm" onClick={handleAddManager}>
                                            <Plus className="w-4 h-4 mr-2" />
                                            Ajouter un responsable
                                        </Button>
                                    </div>
                                    {(formData.managers || []).map((manager, index) => ( // Ensure formData.managers is an array before mapping
                                        <Card key={index} className="p-4">
                                            <div className="grid grid-cols-3 gap-4">
                                                <div>
                                                    <Label>Utilisateur</Label>
                                                    <Select
                                                        value={manager.user_id}
                                                        onValueChange={(value) => {
                                                            const newManagers = [...(formData.managers || [])];
                                                            newManagers[index].user_id = value;
                                                            setFormData({ ...formData, managers: newManagers });
                                                        }}
                                                    >
                                                        <SelectTrigger>
                                                            <SelectValue placeholder="Sélectionner" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {users && users.map(user => (
                                                                <SelectItem key={user.id} value={user.id}>
                                                                    {user.full_name}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <div>
                                                    <Label>Rôle</Label>
                                                    <Select
                                                        value={manager.role}
                                                        onValueChange={(value) => {
                                                            const newManagers = [...(formData.managers || [])];
                                                            newManagers[index].role = value;
                                                            setFormData({ ...formData, managers: newManagers });
                                                        }}
                                                    >
                                                        <SelectTrigger>
                                                            <SelectValue placeholder="Rôle" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {departmentRoleOptions.map(role => (
                                                                <SelectItem key={role.value} value={role.value}>
                                                                    {role.label}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <div className="flex items-end">
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => handleRemoveManager(index)}
                                                        className="text-red-600"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </Card>
                                    ))}
                                </div>

                                <DialogFooter>
                                    <Button type="button" variant="outline" onClick={() => setIsFormOpen(false)}>
                                        Annuler
                                    </Button>
                                    <Button type="submit">
                                        {editingDept ? 'Mettre à jour' : 'Créer'}
                                    </Button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </CardContent>
        </Card>
    );
}
